import React, { useEffect, useState } from 'react';
import { useFirebaseAuth } from '../auth/FirebaseAuthProvider';
import actions from '../actions/actions';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Badge from 'react-bootstrap/Badge';
import { UserAirport } from '../class/UserAirport';
import { Weather } from '../class/Weather';
import './css/Home.css';

interface Props {
  airportMetar: Array<any>;
  airportTaf: Array<any>;
  userAirportList: Array<UserAirport>;
  chatGptReview: any;
  checkUserId(p: any): void;
  checkMetar(p: any): void;
  checkTaf(p: any): void;
  getUserAirportList(token: any): void;
  getChatGptReview(p: any): void;
}

const mapStateToProps = (state: any) => {
  return {
    airportMetar: state.airportMetar,
    airportTaf: state.airportTaf,
    userAirportList: state.userAirportList,
    chatGptReview: state.chatGptReview,
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    checkUserId: (token: any) => dispatch<any>(actions.user.checkUserId(token)),
    checkMetar: (airport: string) => dispatch<any>(actions.weather.getMetar(airport)),
    checkTaf: (airport: string) => dispatch<any>(actions.weather.getTaf(airport)),
    getUserAirportList: (token: any) => dispatch<any>(actions.userAirport.getUserAirportList(token)),
    getChatGptReview: (weather: Weather) => dispatch<any>(actions.weather.getChatGptReview(weather)),
  }
}

function Home(props: Props) {
  const { isAuthenticated, loading, getAccessToken } = useFirebaseAuth();
  const [tafFallback, setTafFallback] = useState<string | null>(null);

  const airportCodes = isAuthenticated && props.userAirportList.length > 0
    ? props.userAirportList.map((a: UserAirport) => a.airport?.airportCode).filter(Boolean)
    : ['ESOW'];

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  useEffect(() => {
    const fetchData = async () => {
      if (loading) return;
      if (!isAuthenticated) {
        props.checkMetar('ESOW');
        props.checkTaf('ESOW');
      } else {
        try {
          const token = await getAccessToken();
          if (!token) return;
          props.checkUserId(token);
          props.getUserAirportList(token);
        } catch (error) {
          console.error('Error fetching token:', error);
        }
      }
    };
    fetchData();
  }, [loading, isAuthenticated]);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      props.checkMetar('ESOW');
      props.checkTaf('ESOW');
      return;
    }
    const airportCode = props.userAirportList.length > 0
      ? props.userAirportList
          .map((airport: UserAirport) => airport.airport?.airportCode)
          .filter(Boolean)
          .join(';')
      : 'ESOW';
    props.checkMetar(airportCode);
    props.checkTaf(airportCode);
  }, [props.userAirportList, isAuthenticated]);

  useEffect(() => {
    if (props.airportTaf.length === 0) {
      setTafFallback('ESOW');
      props.checkTaf('ESSA');
    } else {
      setTafFallback(null);
    }
  }, [props.airportTaf]);

  useEffect(() => {
    const getChatGptReview = async () => {
      if (props.airportMetar.length > 0 &&
          (localStorage.getItem('METAR') !== props.airportMetar[0]?.rawOb || localStorage.getItem('CHATGPTREVIEW') === "")) {
        localStorage.setItem('TAF', props.airportTaf[0]?.rawTAF || '');
        localStorage.setItem('METAR', props.airportMetar[0]?.rawOb || '');
        props.getChatGptReview(new Weather(props.airportMetar[0]?.rawOb, props.airportTaf[0]?.rawTAF, ''));
      }
    };
    getChatGptReview();
  }, [props.airportMetar, props.airportTaf]);

  useEffect(() => {
    if (props.chatGptReview) {
      const review = typeof props.chatGptReview === 'string'
        ? props.chatGptReview
        : props.chatGptReview?.choices?.[0]?.message?.content ?? '';
      if (review) localStorage.setItem('CHATGPTREVIEW', review);
    }
  }, [props.chatGptReview]);

  const cachedReview = localStorage.getItem('CHATGPTREVIEW');

  return (
    <div className="home-container">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <Container>
          <div className="dashboard-header-inner">
            <div className="dashboard-header-left">
              <div className="dashboard-icon">
                <i className="fas fa-cloud-sun"></i>
              </div>
              <div>
                <h1 className="dashboard-title">Weather Briefing</h1>
                <p className="dashboard-date">{today}</p>
              </div>
            </div>
            <div className="dashboard-airports">
              {airportCodes.map((code, i) => (
                <span key={i} className="airport-badge">
                  <i className="fas fa-map-marker-alt me-1"></i>{code}
                </span>
              ))}
            </div>
          </div>
        </Container>
      </div>

      {/* Weather Cards */}
      <Container className="weather-container">
        <Row className="g-4">
          {/* METAR */}
          <Col lg={6}>
            <Card className="data-card h-100">
              <Card.Header className="data-card-header metar-header">
                <div className="card-header-inner">
                  <div className="card-header-title">
                    <i className="fas fa-eye me-2"></i>METAR
                  </div>
                  <Badge className="status-badge">Current Observation</Badge>
                </div>
              </Card.Header>
              <Card.Body className="data-card-body">
                {props.airportMetar.length > 0 ? (
                  <div className="raw-data-list">
                    {props.airportMetar.map((metar: any, index: number) => (
                      <div key={index} className="raw-data-block">
                        <div className="raw-data-airport">{metar.rawOb?.split(' ')[1]}</div>
                        <code className="raw-data-text">{metar.rawOb}</code>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="data-placeholder">
                    <div className="placeholder-spinner">
                      <i className="fas fa-circle-notch fa-spin"></i>
                    </div>
                    <span>Fetching current observations...</span>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* TAF */}
          <Col lg={6}>
            <Card className="data-card h-100">
              <Card.Header className="data-card-header taf-header">
                <div className="card-header-inner">
                  <div className="card-header-title">
                    <i className="fas fa-chart-line me-2"></i>TAF
                  </div>
                  <Badge className="status-badge">Forecast</Badge>
                </div>
              </Card.Header>
              <Card.Body className="data-card-body">
                {props.airportTaf.length > 0 ? (
                  <div className="raw-data-list">
                    {tafFallback && (
                      <div className="fallback-notice">
                        <i className="fas fa-info-circle me-1"></i>
                        {tafFallback} has no TAF available — showing ESSA
                      </div>
                    )}
                    {props.airportTaf.map((taf: any, index: number) => (
                      <div key={index} className="raw-data-block taf">
                        <div className="raw-data-airport">{taf.rawTAF?.split(' ')[1]}</div>
                        <code className="raw-data-text">{taf.rawTAF}</code>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="data-placeholder">
                    <div className="placeholder-spinner">
                      <i className="fas fa-circle-notch fa-spin"></i>
                    </div>
                    <span>Fetching forecast data...</span>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* AI Analysis */}
          <Col lg={12}>
            <Card className="data-card ai-card">
              <Card.Header className="data-card-header ai-header">
                <div className="card-header-inner">
                  <div className="card-header-title">
                    <i className="fas fa-robot me-2"></i>AI Weather Analysis
                  </div>
                  <Badge className="status-badge">Powered by GPT-4</Badge>
                </div>
              </Card.Header>
              <Card.Body className="data-card-body">
                {cachedReview ? (
                  <div className="ai-content">
                    <div className="ai-icon-col">
                      <div className="ai-avatar">
                        <i className="fas fa-brain"></i>
                      </div>
                    </div>
                    <p className="ai-text">{cachedReview}</p>
                  </div>
                ) : (
                  <div className="data-placeholder">
                    <div className="placeholder-spinner">
                      <i className="fas fa-circle-notch fa-spin"></i>
                    </div>
                    <span>AI analysis will appear once weather data is processed...</span>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(Home);
